import {
  get,
  post,
} from 'helpers/RequestUtil'
import {
  call,
  take,
  takeEvery,
  takeLatest,
  select,
  put,
  fork,
  all,
} from 'redux-saga/effects'
import T from 'i18n-react'
import * as R from 'ramda'
import P from 'helpers/predicates'

import {
  POST_UPDATE_PROJECT,
  POST_UPDATE_PROJECT_SUCCESS,
  POST_UPDATE_PROJECT_ERROR,
  POST_WORKFLOW_SUBMIT,
  POST_WORKFLOW_RESTART,
  /* Forms */
  FETCH_FORM_ELEMENTS,
  FETCH_ELEMENT_REFERENCES,
  LOAD_PROJECT_FORM_DATA,
} from './constants'

import {
  LOAD_REVISION_DATA_SUCCESS,
  FETCH_REVISION_DATA_ERROR,
  POST_PROJECT_REVISION_ERROR,
  POST_PROJECT_REVISION_SUCCESS
} from 'containers/Project/Revision/constants'

import {
  POST_PROJECT_SCHEDULE_ERROR,
  POST_PROJECT_SCHEDULE_SUCCESS
} from 'containers/Project/ScheduleSection/constants'
import {
  startPostUpdateProject,
  postUpdateProjectStart,
  postUpdateProjectSuccess,
  postUpdateProjectError,
  startPostWorkflowSubmit,
  postWorkflowSubmitStart,
  postWorkflowSubmitSuccess,
  postWorkflowSubmitError,
  startPostWorkflowRestart,
  postWorkflowRestartStart,
  postWorkflowRestartSuccess,
  postWorkflowRestartError,
  /* Forms */
  fetchFormElements as fetchFormElementsAction,
  fetchFormElementsStart,
  fetchFormElementsSuccess,
  fetchFormElementsError,
  startFetchFormElements,
  fetchElementReferences as fetchElementReferencesAction,
  fetchElementReferencesStart,
  fetchElementReferencesSuccess,
  fetchElementReferencesError,
  startFetchElementReferences,
  updateReferences,
  updateProjectSuccess,
  loadProjectFormDataSuccess,
  loadProjectFormDataStart,
  startLoadProjectFormData,
} from './actions'
import {
  updateTablesAction,
  removeTables,
  clearTables,
} from 'containers/Entity/actions'
import {
  entitySelector,
  resultsSelector,
} from 'containers/Entity/selectors'
import {
  formatProjectData,
  formatRevisionData
} from 'containers/Form/selectors'
import {
  latestRevisionEntity,
} from 'containers/Project/Revision/selectors'
import {
  loadRevision,
  copyFormElements,
} from 'containers/Form/actions'
import {
  initRevisions,
  loadRevisionData,
  updateCurrentRevision,
  updateOldestRevision,
  updateLatestRevision,
  updateOriginalRevision,
  updateModifiedRevision,
  postProjectRevision,
} from 'containers/Project/Revision/actions'


import {
  error,
  success
} from 'containers/App/Notification/actions'
import watchReviewProject from 'containers/Project/Review/sagas'
import watchUpdateReviewProject from 'containers/Project/UpdateReview/sagas'
import watchProjectViewActions from 'containers/Project/View/sagas'
import {
  watchMounting as watchGreenlightCommentMount
} from 'containers/Project/GreenlightCommentSection/sagas'
import {
  watchMounting as watchGreenlightReportMount
} from 'containers/Project/GreenlightReport/sagas'
import watchGreenlightSectionMount from 'containers/Project/GreenlightSection/sagas'
import {
  default as watchRevisions,
  fetchProjectRevisions,
} from 'containers/Project/Revision/sagas'
import {
  postProjectSchedule,
  resetProjectScheduleChange
} from 'containers/Project/ScheduleSection/actions'
import {
  projectScheduleParamsSelector
} from 'containers/Project/ScheduleSection/selectors'

function* postUpdateProject(id, params) {
  try {
    yield put(startPostUpdateProject())
    yield put(postUpdateProjectStart())
    yield call(
      post,
      'updates',
      params,
      '/api/fulcrum/project/updates'
    )

    yield put(postUpdateProjectSuccess())
  } catch(error) {
    yield put(postUpdateProjectError(error))
  }
}

function* postWorkflowSubmit(id, workflowId) {
  try {
    yield put(startPostWorkflowSubmit())
    yield put(postWorkflowSubmitStart())
    yield call(
      post,
      'submits',
      { workflowId },
      '/api/fulcrum/workflow/submits'
    )
    yield put(postWorkflowSubmitSuccess())
    yield put(success(T.translate('pages.project.overview.notification.post_submit', {id})))
  } catch(error) {
    yield put(postWorkflowSubmitError(error))
  }
}

function* postWorkflowRestart(id, workflowId) {
  try {
    yield put(startPostWorkflowRestart())
    yield put(postWorkflowRestartStart())
    yield call(
      post,
      'restarts',
      { workflowId },
      '/api/fulcrum/workflow/restarts'
    )
    yield put(postWorkflowRestartSuccess())
    yield put(success(T.translate('pages.project.overview.notification.post_restart', {id})))
  } catch(error) {
    yield put(postWorkflowRestartError(error))
  }
}

function* fetchFormElements(projectId) {
  try {
    yield put(startFetchFormElements())
    yield put(fetchFormElementsStart())
    const data = yield call(
      get,
      `/api/fulcrum/project/forms/${projectId}/elements`
    )
    const {
      result: { elements }
    } = data
    yield put(updateTablesAction(data))
    yield put(fetchFormElementsSuccess())
    return elements
  } catch(error) {
    yield put(fetchFormElementsError(error))
  }
}

function* fetchElementReferences(projectId, params) {
  try {
    yield put(startFetchElementReferences())
    yield put(fetchElementReferencesStart())
    const models = params.join(',')
    const data = yield call(
      get,
      `/api/fulcrum/project/form/elements/${projectId}` + (models.length > 0 ? `?include=${models}` : '')
    )
    // everything except elements
    const newData = R.compose(
      R.dissocPath(['entities', 'elements']),
      R.dissocPath(['result', 'elements'])
    )(data)
    yield put(updateTablesAction(newData))
    yield put(fetchElementReferencesSuccess())
    return newData
  } catch(error) {
    yield put(fetchElementReferencesError(error))
  }
}

function* performAdditionalPostActions(projectId) {
  const projectScheduleParams = yield select(projectScheduleParamsSelector)
  yield put(postProjectSchedule(projectId, projectScheduleParams))
  const projectScheduleResponse = yield take([POST_PROJECT_SCHEDULE_ERROR, POST_PROJECT_SCHEDULE_SUCCESS])
  if (projectScheduleResponse.type === POST_PROJECT_SCHEDULE_SUCCESS){
    return true
  }
  return false
}

function* updateProject(action) {
  const { workflowId, projectId, isNewProject } = action
  const { form } = yield select()
  const revision = yield select(latestRevisionEntity)
  const form_data = formatProjectData(form)
  const revision_data = formatRevisionData({ form, revision })
  yield fork(postUpdateProject, projectId, { workflowId, form_data })
  const projectResponse = yield take([POST_UPDATE_PROJECT_SUCCESS, POST_UPDATE_PROJECT_ERROR])
  if (projectResponse.type === POST_UPDATE_PROJECT_SUCCESS) {
    yield put(postProjectRevision(projectId, { projectId, data: revision_data }))
    const revisionResponse = yield take([POST_PROJECT_REVISION_ERROR, POST_PROJECT_REVISION_SUCCESS])
    if (revisionResponse.type === POST_PROJECT_REVISION_SUCCESS)
      var success = true
      if(isNewProject){
        success = yield call(performAdditionalPostActions, projectId)
      }
      if(success){
        yield put(updateProjectSuccess())
      }
  }
}

function* getReferences(ids) {
  const {
    elements: elementEntities
  } = yield select(entitySelector)

  const elementValues = R.map(id => elementEntities[id])(ids)
  const references = R.reduce((final, element) => {
    let properties = R.values(element.properties)
    properties = R.filter(({ type }) => type === 'reference')(properties)
    let refs = R.map(({ ref }) => ref)(properties)
    return R.concat(refs, final)
  }, [])(elementValues)

  return references
}

function* getRevision(ids) {
  const {
    project_revisions: projectRevisions,
  } = yield select(entitySelector)

  return R.compose(
    R.map(id => projectRevisions[id].revision),
    (array) => [R.head(array), R.last(array)]
  )(ids)
}

function* formatElementRefs() {
  const { elements } = yield select(entitySelector)
  const results = yield select(resultsSelector)

  const refsToEntities = ([name, property]) => {
    let newProperty = property
    if (property.type === 'reference') {
      const refIds = results[property.ref]
      if (refIds) newProperty = R.map(id => ({type: property.ref, id }))(refIds)
    }
    return [name, newProperty]
  }

  const mapProperties = R.compose(
    R.fromPairs,
    R.map(refsToEntities),
    R.toPairs
  )

  const propertiesToEntities = ([id, { properties, ...rest }]) => {
    const newProperties = mapProperties(properties)
    return [id, { ...rest, ...newProperties }]
  }

  return R.compose(
    (elements) => ({
      result: {
        elements: R.keys(elements)
      },
      entities: {
        elements
      }
    }),
    R.fromPairs,
    R.map(propertiesToEntities),
    R.toPairs
  )(elements)
}

function* handleLoadRevisions(projectId, first, last) {
  yield put(loadRevisionData(projectId, last))
  let action = yield take([LOAD_REVISION_DATA_SUCCESS, FETCH_REVISION_DATA_ERROR])

  if (first !== last) {
    yield put(loadRevisionData(projectId, first))
    action = yield take([LOAD_REVISION_DATA_SUCCESS, FETCH_REVISION_DATA_ERROR])
  }

  if (action.type === LOAD_REVISION_DATA_SUCCESS) {
    yield put(updateOldestRevision(last))
    yield put(updateLatestRevision(first))
    yield put(updateOriginalRevision(last))
    yield put(updateModifiedRevision(first))
    yield put(copyFormElements(R.clone(action.elements)))
  }
}

function* loadProjectForm({ projectId }) {
  try {
    yield put(startLoadProjectFormData())
    yield put(loadProjectFormDataStart())
    yield put(initRevisions())
    yield put(resetProjectScheduleChange())
    const [elementIds, projectRevisionIds] = yield all([
      call(fetchFormElements, projectId),
      call(fetchProjectRevisions, projectId)
    ])
    const revisionId = yield call(getRevision, projectRevisionIds)
    const refs = yield call(getReferences, elementIds)
    yield call(fetchElementReferences, projectId, refs)

    const [first, last] = revisionId
    yield put(updateReferences(refs))
    const data = yield call(formatElementRefs)
    yield put(updateTablesAction(data))
    yield call(handleLoadRevisions, projectId, first, last)
    yield put(loadProjectFormDataSuccess())
  } catch(error) {
    console.log('ERROR: ', error)
  }
}

function* handlePostWorkflowSubmit(action) {
  const { workflowId, projectId } = action
  yield call(postWorkflowSubmit, projectId, workflowId)
}

function* handlePostWorkflowRestart(action) {
  const { workflowId, projectId } = action
  yield call(postWorkflowRestart, projectId, workflowId)
}

function* handleFetchFormElements({ projectId }) {
  const elements = yield call(fetchFormElements, projectId)
  return elements
}

function* handleFetchElementReferences({ projectId, params }) {
  const references = yield call(fetchElementReferences, projectId)
  return references
}

function* watchUpdateProject() {
  yield takeEvery(POST_UPDATE_PROJECT, updateProject)
}

function* watchPostWorkflowSubmit() {
  yield takeLatest(POST_WORKFLOW_SUBMIT, handlePostWorkflowSubmit)
}

function* watchPostWorkflowRestart() {
  yield takeLatest(POST_WORKFLOW_RESTART, handlePostWorkflowRestart)
}

function* watchFetchFormElements() {
  yield takeEvery(FETCH_FORM_ELEMENTS, handleFetchFormElements)
}

function* watchFetchElementReferences() {
  yield takeEvery(FETCH_ELEMENT_REFERENCES, handleFetchElementReferences)
}

function* watchLoadProjectFormData() {
  yield takeLatest(LOAD_PROJECT_FORM_DATA, loadProjectForm)
}

function* loadSagas() {
  yield all([
    fork(watchUpdateProject),
    fork(watchPostWorkflowSubmit),
    fork(watchReviewProject),
    fork(watchUpdateReviewProject),
    fork(watchProjectViewActions),
    fork(watchPostWorkflowRestart),
    fork(watchGreenlightSectionMount),
    fork(watchGreenlightReportMount),
    fork(watchGreenlightCommentMount),
    fork(watchFetchFormElements),
    fork(watchFetchElementReferences),
    fork(watchLoadProjectFormData),
    fork(watchRevisions),
  ])
}

export {
  loadSagas
}
